import { FC, Fragment, useEffect, useState } from 'react';
import { FlightFilterModel } from 'ts/flight/classes/FlightFilterModel';
import { Range } from 'react-range';
import withErrorBoundary from 'components/shared/withErrorBoundary';

interface FlightResultFilterSliderProps {
  filter: FlightFilterModel;
  setFilter: (filter: FlightFilterModel) => void;
  minStr: string;
  maxStr: string;
  scale?: number;
  type:
    | 'price'
    | 'duration'
    | 'departureInboundTime'
    | 'departureOutboundTime'
    | 'stopDuration'
    | 'arrivalInboundTime'
    | 'arrivalOutboundTime';
  onlyLtr?: boolean;
}

const FlightResultFilterSlider: FC<FlightResultFilterSliderProps> = ({
  filter,
  setFilter,
  type,
  scale = 1,
  onlyLtr = false,
  ...props
}) => {
  const colorContrast = getComputedStyle(document.body).getPropertyValue('--color-contrast');
  const [values, setValues] = useState(() => {
    const initialValue = filter[type]?.value;
    const filterType = filter[type];
    if (initialValue && filterType?.min !== undefined && filterType?.max !== undefined) {
      return [
        Math.max(filterType.min, Math.min(filterType.max, initialValue[0])),
        Math.max(filterType.min, Math.min(filterType.max, initialValue[1])),
      ];
    }
    return initialValue;
  });

  useEffect(() => {
    const newValue = filter[type]?.value;
    const filterType = filter[type];
    if (newValue && filterType) {
      const clampedValue: [number, number] = [
        Math.max(filterType.min || 0, Math.min(filterType.max || 1, newValue[0])),
        Math.max(filterType.min || 0, Math.min(filterType.max || 1, newValue[1])),
      ];
      setValues(clampedValue);
    }
  }, [filter[type]?.value, filter[type]?.min, filter[type]?.max]); // eslint-disable-line react-hooks/exhaustive-deps

  return (
    <div>
      {filter[type]?.min !== filter[type]?.max && (
        <Fragment>
          <div className="flight-filter-time text-lh-sm">
            <span>{props.minStr}</span>
            <span>{props.maxStr}</span>
          </div>
          <Range
            step={1}
            rtl={onlyLtr ? false : document.body.classList.contains('rtl')}
            min={filter[type]?.min}
            max={filter[type]?.max}
            onFinalChange={() =>
              setFilter({ ...filter, [type]: { ...filter[type], value: values } })
            }
            values={
              values &&
              values[0] >= (filter[type]?.min || 0) &&
              values[1] <= (filter[type]?.max || 1)
                ? values
                : [filter[type]?.min || 0, filter[type]?.max || 1]
            }
            onChange={(values) => setValues(values as [number, number])}
            renderTrack={({ props, children }) => (
              <div
                {...props}
                style={{
                  ...props,
                  height: scale * 4 + 'px',
                  width: 'calc(100% - 16px)',
                  margin: '0 8px',
                  backgroundColor: '#ccc',
                }}
              >
                {children}
              </div>
            )}
            renderThumb={({ props }) => (
              <div
                {...props}
                style={{
                  ...props,
                  position: 'absolute',
                  height: scale * 16 + 'px',
                  width: scale * 16 + 'px',
                  borderRadius: '50%',
                  backgroundColor: colorContrast,
                }}
              />
            )}
          />
          <div className="mb-20"></div>
        </Fragment>
      )}
    </div>
  );
};

export default withErrorBoundary(FlightResultFilterSlider, 'FlightResultFilterSlider');
